/**
 * Frontend Call Graph — first-class artifact representing the frontend half
 * of the E2E flow analyzer. See veodiagram issue #803 (Phase 1.1 of the
 * stitching accuracy initiative, epic #802).
 *
 * The graph has four node kinds:
 *   - UIElement     — something the user can interact with (button, form, link)
 *   - Handler       — the function that runs in response to a UI event
 *   - Function      — an intermediate function on the path from handler to call site
 *   - Hook          — a custom React hook on the path
 *   - ApiCallSite   — a leaf node: the concrete fetch/axios/etc. call
 *
 * Edges are `invokes` (UI -> Handler) or `calls` (anything -> anything downstream).
 *
 * Baseline (this issue): builder is a pure refactor. It reuses existing
 * detectors (Tier-1 interaction scanner, apiCallTracer) and the Tier-1
 * handler walk in tier1Orchestrator, producing a graph that is a superset
 * of what Tier 1 already generates today. No URL-extraction improvements, no
 * wrapper chain tracking, no stitching changes — those land in #804, #805,
 * and #811 respectively.
 */
import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Structural keying (used by #810 StitchSet) =============
/**
 * Structural key for a call site that survives refactors. Phase 3 (#810)
 * will use this to reattach user-authored stitches after re-analysis even
 * when files move or functions are renamed. Populated with best-effort
 * values in the baseline; `wrapperChainHash` is an empty string until
 * wrapper chain tracking lands in #805.
 */
export const CallSiteKeySchema = z.object({
    /** Repo-relative, normalized with forward slashes. */
    filePath: z.string(),
    /** Dotted symbol path of the enclosing function, e.g. 'UserForm.handleSubmit'. */
    enclosingSymbol: z.string(),
    /** URL pattern as extracted from the source. May be '{dynamic}'. */
    urlPattern: z.string(),
    /** HTTP method (normalized uppercase) or 'inferred'/'unknown'. */
    method: z.string(),
    /** Hash of the wrapper chain for disambiguation. Empty string in the baseline. */
    wrapperChainHash: z.string(),
});
// ============= ApiCallSite (leaf node) =============
export const ApiCallSiteSchema = z.object({
    /** Unique within this graph. For the baseline this is the ApiCall.id from apiCallTracer. */
    callSiteId: z.string(),
    /** Structural key for cross-analysis stability. */
    callSiteKey: CallSiteKeySchema,
    /** Source file relative to repo root. */
    filePath: z.string(),
    /** First line of the call expression. */
    lineStart: z.number().int().nonnegative(),
    /** Last line of the call expression (may equal lineStart for single-line calls). */
    lineEnd: z.number().int().nonnegative(),
    /** URL pattern as extracted. May be '{dynamic}' if unresolvable. */
    urlPattern: z.string(),
    /** HTTP method, uppercase, or 'inferred'/'unknown'. */
    method: z.string(),
    /** Which library produced this call (fetch, axios, supabase-js, ...). */
    library: z.string(),
    /**
     * Wrapper chain from the handler down to the concrete sink.
     * Empty in the baseline; populated by #805 (wrapper chain tracking).
     */
    wrapperChain: z.array(z.string()).default([]),
    /** Single-wrapper name from the existing ApiCall model, if any. */
    wrapperFunction: z.string().optional(),
    /**
     * Surrounding source context for AI-assisted stitching (Phase 5, #813).
     * Empty in the baseline.
     */
    surroundingSource: z.string().default(''),
    /** Confidence of the extraction itself (distinct from any matching confidence). */
    extractionConfidence: z.number().min(0).max(1).default(1),
    /** Which detector produced this call site (e.g. 'apiCallTracer'). */
    extractor: z.string(),
    /** True if the URL could not be statically resolved to a concrete pattern. */
    isDynamicEndpoint: z.boolean(),
    /** Code evidence array in the same shape as existing diagram evidence. */
    evidence: z.array(CodeEvidenceSchema).default([]),
});
// ============= Node kinds =============
export const FCGNodeKindSchema = z.enum([
    'UIElement',
    'Handler',
    'Function',
    'Hook',
    'ApiCallSite',
]);
export const UIElementNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('UIElement'),
    /** Component that contains this UI element. */
    component: z.string().optional(),
    /** Interaction type: 'click', 'submit', 'navigate', 'fetch', 'realtime', 'file', 'gesture'. */
    interactionType: z.string(),
    /** DOM element type: 'button', 'form', 'a', ... */
    triggerElement: z.string().optional(),
    /** Event type: 'onClick', 'onSubmit', ... */
    eventType: z.string().optional(),
    /** Visible text (button label, link text) if available. */
    elementText: z.string().optional(),
    filePath: z.string(),
    line: z.number().int().nonnegative(),
    /** ID of the backing UserInteraction from the Tier-1 interaction scanner. */
    interactionId: z.string(),
});
export const HandlerNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('Handler'),
    handlerName: z.string(),
    filePath: z.string(),
    line: z.number().int().nonnegative(),
});
export const FunctionNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('Function'),
    functionName: z.string(),
    filePath: z.string().optional(),
});
export const HookNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('Hook'),
    hookName: z.string(),
    filePath: z.string().optional(),
});
export const ApiCallSiteNodeSchema = z.object({
    id: z.string(),
    kind: z.literal('ApiCallSite'),
    callSite: ApiCallSiteSchema,
});
export const FCGNodeSchema = z.discriminatedUnion('kind', [
    UIElementNodeSchema,
    HandlerNodeSchema,
    FunctionNodeSchema,
    HookNodeSchema,
    ApiCallSiteNodeSchema,
]);
// ============= Edges =============
export const FCGEdgeKindSchema = z.enum(['invokes', 'calls']);
export const FCGEdgeSchema = z.object({
    from: z.string(),
    to: z.string(),
    kind: FCGEdgeKindSchema,
});
// ============= Stats =============
export const FCGStatsSchema = z.object({
    uiElements: z.number().int().nonnegative(),
    handlers: z.number().int().nonnegative(),
    functions: z.number().int().nonnegative(),
    hooks: z.number().int().nonnegative(),
    callSites: z.number().int().nonnegative(),
    dynamicCallSites: z.number().int().nonnegative(),
});
// ============= Top-level graph =============
export const FrontendCallGraphSchema = z.object({
    projectId: z.string(),
    /** Schema version. Bump when the shape changes in a backwards-incompatible way. */
    version: z.literal(1),
    /** ISO 8601 timestamp of when the graph was generated. */
    generatedAt: z.string(),
    /** All nodes keyed by node id. */
    nodes: z.record(z.string(), FCGNodeSchema),
    /** All edges. */
    edges: z.array(FCGEdgeSchema),
    /** Denormalized index of leaf call sites for fast iteration (Phase 3 / #811 uses this). */
    apiCallSites: z.array(ApiCallSiteSchema),
    stats: FCGStatsSchema,
});
//# sourceMappingURL=frontendCallGraph.js.map